import { Component } from 'vue';
import { VuetifyOptions } from 'vuetify';
import { aliases } from 'vuetify/iconsets/mdi';

import { HAULIER_NAME } from '@/config/config';
import { HAULIERS } from '@/constants/Constants';

import { adosaTheme } from '@/themes/adosa/theme';
import { dpbTheme } from '@/themes/dpb/theme';
import { dpmdasTheme } from '@/themes/dpmdas/theme';
import { dpmlbTheme } from '@/themes/dpmlb/theme';
import { dpmoTheme } from '@/themes/dpmo/theme';
import { dpmpIcons } from '@/themes/dpmp/icons';
import { dpmprTheme } from '@/themes/dpmpr/theme';
import { dpmpTheme } from '@/themes/dpmp/theme';
import { jikordIcons } from '@/themes/jikord/icons';
import { jikordTheme } from '@/themes/jikord/theme';
import { nitraTheme } from '@/themes/nitra/theme';
import { polandTheme } from '@/themes/poland/theme';
import { ruzomberokTheme } from '@/themes/ruzomberok/theme';
import { trencinTheme } from '@/themes/trencin/theme';
import { viseudesusTheme } from '@/themes/viseudesus/theme';
import { localTheme } from '@/themes/local/theme';

export const defaultAppIcons = {
  account: 'mdi-account',
  accountSettingsIcon: 'mdi-account-cog',
  assignCardIcon: 'mdi-credit-card-plus-outline',
  assignCustodianIcon: 'mdi-account-child',
  assignedCardListIcon: 'mdi-human-male-child',
  bankCardApplicationIcon: 'mdi-card-account-details-outline',
  cancelAllIcon: 'mdi-cancel',
  cardApplicationListIcon: 'mdi-form-select',
  cardManagingAccountIcon: 'mdi-card-account-details-outline',
  chipCardIcon: 'mdi-card-account-details-outline',
  electronicWalletIcon: 'mdi-cash-multiple',
  externalCardApplicationIcon: 'mdi-card-account-details-outline',
  forgottenPasswordIcon: 'mdi-email-outline',
  loginIcon: 'mdi-login',
  logoutIcon: 'mdi-logout',
  manageCardRequestIcon: 'mdi-card-plus-outline',
  managedCardListIcon: 'mdi-account-group',
  menuIcon: 'mdi-menu',
  newCardApplicationIcon: 'mdi-card-account-details-outline',
  notificationSettingIcon: 'mdi-bell',
  offenseListIcon: 'mdi-gavel',
  orderListIcon: 'mdi-cash-multiple',
  pakTransferIcon: 'mdi-wallet-plus-outline',
  payByCardIcon: 'mdi-credit-card-outline',
  payByInvoiceIcon: 'mdi-bank-transfer',
  pencil: 'mdi-pencil',
  profileApplicationListIcon: 'mdi-account-multiple',
  reclamationIcon: 'mdi-repeat',
  registrationSendIcon: 'mdi-account-plus',
  settingsIcon: 'mdi-cog',
  shoppingCartIcon: 'mdi-cart-outline',
  shoppingCartTopIcon: 'mdi-cart-outline',
  thirdPartyTicketIcon: 'mdi-ticket',
  ticketFareIcon: 'mdi-ticket-account',
  vehicleTransactionListIcon: 'mdi-deskphone',
  verificationCodeIcon: 'mdi-email-outline',
};

const defaultThemeVariables = {
  'disabled-opacity': '0.55',
  'button-prepend-svg-height': '32px',
  'button-prepend-svg-width': '32px',
};

// To add a new theme, refer to dpmpTheme as a reference where all color properties are documented
export const getHaulierTheme = (): VuetifyOptions['theme'] => {
  switch (HAULIER_NAME) {
    case HAULIERS.ADOSA:
      return adosaTheme;
    case HAULIERS.DPB:
      return dpbTheme;
    case HAULIERS.DPMDAS:
      return dpmdasTheme;
    case HAULIERS.DPMLB:
      return dpmlbTheme;
    case HAULIERS.DPMO:
      return dpmoTheme;
    case HAULIERS.DPMP:
      return dpmpTheme;
    case HAULIERS.DPMPR:
      return dpmprTheme;
    case HAULIERS.LOCAL:
      return localTheme;
    case HAULIERS.NITRA:
      return nitraTheme;
    case HAULIERS.JIKORD:
      return jikordTheme;
    case HAULIERS.POLAND:
      return polandTheme;
    case HAULIERS.RUZOMBEROK:
      return ruzomberokTheme;
    case HAULIERS.TRENCIN:
      return trencinTheme;
    case HAULIERS.VISEUDESUS:
      return viseudesusTheme;
    default:
      throw new Error(`Haulier theme ${HAULIER_NAME} is not defined. Did you forget to define provider theme?`);
  }
};

export const getTheme = () => {
  const haulierTheme = getHaulierTheme();
  if (typeof haulierTheme !== 'object') {
    throw new TypeError('Haulier theme is not an object');
  }

  if (typeof haulierTheme.themes !== 'object') {
    throw new TypeError('Haulier themes is not an object. Did you forget to define color themes?');
  }

  const themes = { ...haulierTheme.themes };
  for (const [themeName, theme] of Object.entries(haulierTheme.themes)) {
    themes[themeName] = {
      ...theme,
      variables: {
        ...defaultThemeVariables,
        ...theme?.variables,
      },
    };
  }

  return {
    ...haulierTheme,
    themes,
  };
};

export const getIcons = () => {
  switch (HAULIER_NAME) {
    case HAULIERS.DPMP:
      return { ...defaultAppIcons, ...dpmpIcons };
    case HAULIERS.JIKORD:
      return { ...defaultAppIcons, ...jikordIcons };
    default:
      return defaultAppIcons;
  }
};

export const getIconsAliases = () => {
  const base = {
    ...aliases,
    ...defaultAppIcons,
  };

  switch (HAULIER_NAME) {
    case HAULIERS.DPMP:
      return { ...base, ...getHaulierIconAliases(dpmpIcons) };
    case HAULIERS.JIKORD:
      return { ...base, ...getHaulierIconAliases(jikordIcons) };
    default:
      return base;
  }
};

const getHaulierIconAliases = (haulierIcons: Record<string, Component>) =>
  Object.keys(haulierIcons).reduce(
    (acc, key) => {
      acc[key] = `custom:${key}`;
      return acc;
    },
    {} as { [key: string]: string },
  );
